import React, { useEffect } from 'react';
import { utcToZonedTime } from 'date-fns-tz';
import usePublicMeetingLink from '../../../hooks/api/public/use-public-meeting-link';
import useMeetingLinkPath from '../../../hooks/use-meeting-link-path';
import useBypassToken from '../../../hooks/use-bypass-token';
import { useTemplateProcessor } from '../../../hooks/use-template-processor';
import {
  BACKGROUND_IMG_URL,
  ERROR_MESSAGE,
  PassportNonce,
  PRIMARY_COLOR
} from '../../../lib/constants';
import { MeetingOrganizationDetailsListType, PublicMeetingDetailsResponseType } from '../../../lib/api';
import { getEventOffsetGMT, TimeZoneDisplayTypeLabels } from '../../../lib/helpers/dateHelper';
import EventDetails from '../../shared/EventDetails';
import MeetingTimingBlock from '../../shared/MeetingTimingBlock';
import MeetingDetailsLinks from '../../shared/MeetingDetailsLinks/MeetingDetailsLinks';
import SafeHTMLRender from '../../shared/SafeHTMLRender';
import ConnectionWrapper from '../../passport/MeetingWrapper/ConnectionWrapper/ConnectionWrapper';
import '../../shared/MeetingDetails/MeetingDetails.scss';
import '../../passport/MeetingWrapper/MeetingWrapper.scss';
import './meeting-link-details-wrapper.scss';

const LANDING_PAGE_CLASS = 'meeting-link-landing-active';
export const INVALID_MEETING_LINK_PATH_MESSAGE = 'Invalid meeting path url';

type MeetingLinkLandingDetails = PublicMeetingDetailsResponseType & {
  presenterOrganizations?: MeetingOrganizationDetailsListType;
};

export const getMeetingLinkDocumentTitle = (details: MeetingLinkLandingDetails): string => {
  return [
    details.topic,
    getPresentingOrganizationNames(details.presenterOrganizations),
    details.eventTitleForPassport,
    details.eventName,
  ].map(part => part?.trim()).find(Boolean) ?? '';
};

const getPresentingOrganizationNames = (organizations?: MeetingOrganizationDetailsListType): string => {
  if (!organizations?.length) {
    return '';
  }
  return organizations.map(organization => organization.name?.trim()).filter(Boolean).join(', ');
};

type StatusMessageProps = {
  message: string;
}

const MeetingLinkStatusMessage: React.FC<StatusMessageProps> = ({ message }) => (
  <div className='md:w-3/4 w-100 mx-auto' data-testid='meeting-link-status'>
    <div className="flex flex-col items-center justify-center h-64 mt-16">
      <div className="flex mt-40 text-red-500 font-medium text-center break-all">
        {message}
      </div>
      <img className="background-img" src={BACKGROUND_IMG_URL} alt='' />
    </div>
  </div>
);

const getAppRoot = (): HTMLDivElement | null => document.querySelector('[data-id="app"]');

export const MeetingLinkLandingWrapper: React.FC = (): JSX.Element | null => {
  const { data, error } = usePublicMeetingLink();
  const meetingLinkPath = useMeetingLinkPath();
  const token = useBypassToken();
  const templateProcessor = useTemplateProcessor();
  const isConnectBtnDisabled = !!data && Date.parse(data.endDateTime) < Date.now();
  const hasMeetingData = !!data;
  const meetingPrimaryColor = data?.primaryColor;

  useEffect(() => {
    const appRoot = getAppRoot();
    appRoot?.classList.add(LANDING_PAGE_CLASS);

    return () => {
      appRoot?.classList.remove(LANDING_PAGE_CLASS);
    };
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }
    const title = getMeetingLinkDocumentTitle(data);
    if (!title) {
      return;
    }
    // Layout sets document.title in a parent effect that runs after this one.
    const timer = window.setTimeout(() => {
      document.title = title;
    }, 0);
    return () => window.clearTimeout(timer);
  }, [data]);

  useEffect(() => {
    const appRoot = getAppRoot();
    if (!appRoot || !hasMeetingData) {
      return;
    }

    appRoot.style.setProperty('--primary-color', meetingPrimaryColor || PRIMARY_COLOR);

    return () => {
      appRoot.style.removeProperty('--primary-color');
    };
  }, [hasMeetingData, meetingPrimaryColor]);

  if (!meetingLinkPath || !token) {
    return <MeetingLinkStatusMessage message={INVALID_MEETING_LINK_PATH_MESSAGE} />;
  }

  if (error && !data) {
    return <MeetingLinkStatusMessage message={error?.message || ERROR_MESSAGE} />;
  }

  const eventInfo = {
    startTime: data?.eventStartDateTime || '',
    endTime: data?.eventEndDateTime || '',
    name: data?.eventName || '',
    timeZone: data?.timeZone || '',
    logoTitle: data?.logoTitle || '',
    logoUrl: data?.logoUrl || '',
    eventTitleForPassport: data?.eventTitleForPassport || '',
    timeZoneDisplayType: data?.timeZoneDisplayType || '',
    customTimeZoneDisplay: data?.customTimeZoneDisplay || '',
  };
  const getTransformedTimeZone = (): string => {
    if (!data) {
      return '';
    }

    const eventStartDateTime = utcToZonedTime(data.eventStartDateTime, data.timeZone);
    const timeZoneLabel = getEventOffsetGMT(
      data.timeZoneDisplayType as TimeZoneDisplayTypeLabels,
      data.customTimeZoneDisplay as string,
      eventStartDateTime,
      data.timeZone
    );

    return data.timeZoneDisplayType === TimeZoneDisplayTypeLabels.Custom
      ? templateProcessor(timeZoneLabel)
      : timeZoneLabel;
  };
  const transformedTimeZone = getTransformedTimeZone();

  return (
    <>
      {data && <EventDetails details={eventInfo} />}
      {data && <div className='md:w-3/4 w-100' data-testid='meeting-link-landing'>
        <div className='shadow-gray bg-white mx-auto md:px-8 px-0 p-8 mt-12'>
          <div className='block sm:flex flex-none sm:flex-row items-start items-stretch max-h-full min-h-full'>
            <div className='flex-equal sm:flex-1 px-6 md:px-0 py-6 sm:pb-0 overflow-visible md:w-1/2'>
              <div className='pt-2 xs:pt-8 pb-5 sm:pl-8 sm:pr-12'>
                {data &&
                  <MeetingTimingBlock
                    meetingDetails={data}
                    timeZone={data.timeZone}
                    transformedTimeZone={transformedTimeZone}
                    localTimeFontSize='font-size-12px'
                  />}
              </div>
              {(data?.topic || data?.notes) && <div className='pt-3 pb-5 px-0 sm:pl-8 sm:pr-12'>
                <h2 className='text-primary meeting-topic font-semibold font-size-18px pb-1'>{data.topic}</h2>
                <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={data.notes} className='text-sm whitespace-pre-wrap break-words' />
              </div>}
              {data && data.links?.length > 0 &&
                <MeetingDetailsLinks links={data.links} />
              }
            </div>
            {data &&
              <ConnectionWrapper
                meetingDetails={data}
                isConnectBtnDisabled={isConnectBtnDisabled}
                eventId={String(data.eventId ?? '')}
              />
            }
          </div>
        </div>
      </div>}
      {data &&
        <img
          className='background-img'
          src={data.backgroundFile || BACKGROUND_IMG_URL}
          alt={data.backgroundAltText || data.backgroundTitle || ''}
        />}
    </>
  );
};

export default MeetingLinkLandingWrapper;
